import { getApiErrorData, isFetchBaseQueryError } from "_shared/_errors/api-error-message";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { ProposalIssue, ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { Masked } from "work/masked-fields";

/**
 * A draft save the API refused, as issues on the fields that fix it.
 *
 * The API says which team a refusal is about, in `errors` keyed by the field (`vendorTeam.leader`,
 * `clientTeam`, ...). The snackbar carries the sentence once and is gone; these keep it on the field,
 * where the user goes to fix it, and count as issues, so Save stays disabled until they do.
 *
 * Only the team refusals are read. Their text is the API's own reviewed prose (`DraftRefusal`), and a
 * team is something this page can point at. The shared `validation_failed` body is not read: its
 * every message is "The value is invalid.", which tells the user nothing.
 *
 * A refusal holds only while both teams are as they were when it was made. Any change to either one
 * is the user acting on it, and the next save asks the API again.
 */
export default class DraftSaveRefusal {
  public static fromError(error: unknown, builder: ProposalBuilder | null | undefined): DraftSaveRefusal | undefined {
    if (!builder || !isFetchBaseQueryError(error)) return undefined;
    const issues = teamErrorsOf(getApiErrorData(error)).flatMap(([key, messages]) => {
      const field = TEAM_ERROR_FIELDS[key](builder);
      return messages.map((message) => new ProposalIssue(field, message, ProposalIssueLevel.Critical));
    });
    return issues.length > 0 ? new DraftSaveRefusal(issues, teamsKeyOf(builder)) : undefined;
  }

  private constructor(
    public readonly issues: readonly ProposalIssue[],
    private readonly teamsKey: string,
  ) {}

  public appliesTo(builder: ProposalBuilder | null | undefined): boolean {
    return Boolean(builder) && teamsKeyOf(builder!) === this.teamsKey;
  }
}

/** The field each team error key is fixed on. */
const TEAM_ERROR_FIELDS: Readonly<Record<string, (builder: ProposalBuilder) => ProposalField>> = {
  // The Client Team tab shows no issues; the Client Representative on Details is where it is fixed.
  clientTeam: () => ProposalField.Client,
  "clientTeam.leader": () => ProposalField.Client,
  vendorTeam: () => ProposalField.Team,
  "vendorTeam.leader": (builder) => {
    const vendorTeam = builder.currentSpec.workAgreement.vendorTeam;
    return ProposalField.TeamLeader(Masked.isPresentNotMasked(vendorTeam) ? vendorTeam.leader?.userId : undefined);
  },
};

function teamErrorsOf(data: unknown): [string, string[]][] {
  if (typeof data !== "object" || data === null) return [];
  if ("code" in data && data.code === "validation_failed") return [];
  if (!("errors" in data) || typeof data.errors !== "object" || data.errors === null) return [];
  return Object.entries(data.errors as Record<string, unknown>).flatMap(([key, messages]): [string, string[]][] => {
    if (!Object.hasOwn(TEAM_ERROR_FIELDS, key) || !Array.isArray(messages)) return [];
    return [[key, messages.filter((message): message is string => typeof message === "string" && message.trim() !== "")]];
  });
}

function teamsKeyOf(builder: ProposalBuilder): string {
  const { clientTeam, vendorTeam } = builder.currentSpec.workAgreement;
  return JSON.stringify([clientTeam ?? null, vendorTeam ?? null]);
}
